import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { Subscription, of } from 'rxjs'
import { finalize, map, switchMap, catchError } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from '../../services/account.service'
import { ApplicationProviderService } from '../../services/application.service'
import { SyncPairProviderService } from '../../services/sync-pair.service'
import { SyncSourceProviderService } from '../../services/sync-source.service'
import { DomSanitizer } from '@angular/platform-browser'
import { AlertService } from 'src/app/arpio/arpio-alert/services/alert.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { OnboardingService } from '../../services/onboarding.service'
import { CapacityBannerService, CapacityThreshold } from '../../services/capacity-banner.service'

@Component({
  selector: 'app-account-shell',
  templateUrl: './account-shell.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AccountShellComponent implements OnInit, OnDestroy {
  private _accountIdSubscription: Subscription = null
  private _accountLoadingSubscription: Subscription = null
  private _appRefreshSubscription: Subscription = null

  public accountId: string = null
  public awsOutageObj$ = this.alertService.data$.pipe(
    map((data) => {
      const item = data.find((item) => item.category === 'outage' && item.visible)
      if (item) {
        return { ...item, safeHtml: this.sanitizer.bypassSecurityTrustHtml(item.message) }
      }
      return undefined
    })
  )

  constructor(
    private activatedRoute: ActivatedRoute,
    public uiService: UiService,
    private apiService: ApiService,
    public accountService: AccountService,
    private applicationProviderService: ApplicationProviderService,
    private syncPairProviderService: SyncPairProviderService,
    private syncSourceProviderService: SyncSourceProviderService,
    private alertService: AlertService,
    private sanitizer: DomSanitizer,
    private accessLevelService: AccessLevelService,
    private onboardingService: OnboardingService,
    public capacityBannerService: CapacityBannerService
  ) {}

  /** Capacity-banner state (100% / above 105%) or null; cheap per change-detection pass, dismissals are in memory. */
  get capacityBanner() {
    return this.capacityBannerService.getState()
  }

  dismissCapacityBanner(threshold: CapacityThreshold) {
    this.capacityBannerService.dismiss(threshold)
  }

  ngOnInit() {
    this._accountIdSubscription = this.activatedRoute.paramMap.subscribe((params) => {
      if (params.has('accountId')) {
        this.accountId = params.get('accountId')

        // Set account ID for services that use per-account localStorage
        this.onboardingService.setAccountId(this.accountId)

        // Invalidate the application cache and mark not-ready BEFORE loadAccount$ fires
        // accountIdSource.next() — otherwise AccountRootComponent can see a stale ready=true
        // from the previous account and route to the welcome page with an empty cache.
        this.applicationProviderService.prepareForAccountLoad(this.accountId)

        this._accountLoadingSubscription = this.accountService
          .loadAccount$(this.accountId)
          .pipe(
            switchMap((_accountDto) => {
              // Try to load applications, but handle 403 errors gracefully
              return this.applicationProviderService.loadAllApplications$(this.accountService.accountId).pipe(
                catchError((error) => {
                  // If we get a 403, it means the user doesn't have application access
                  // This is fine - just continue without loading applications
                  if (error.status === 403) {
                    console.log('User does not have application access, skipping application loading')
                    return of(null)
                  }
                  // For other errors, rethrow
                  throw error
                })
              )
            }),
            finalize(() => {
              this._accountLoadingSubscription = null
            })
          )
          .subscribe(
            (_application) => {
              this._appRefreshSubscription = this.applicationProviderService
                .startBackgroundRefreshForAllApps$()
                .subscribe()
            },
            handleArpioError(window.location.href, this.apiService, this.uiService, { redirectOn403: true })
          )
      }
    })
  }

  get initialized() {
    return !subscriptionActive(this._accountLoadingSubscription)
  }

  ngOnDestroy() {
    if (subscriptionActive(this._accountLoadingSubscription)) {
      this._accountLoadingSubscription.unsubscribe()
      this._accountLoadingSubscription = null
    }
    if (subscriptionActive(this._accountIdSubscription)) {
      this._accountIdSubscription.unsubscribe()
      this._accountIdSubscription = null
    }
    if (subscriptionActive(this._appRefreshSubscription)) {
      this._appRefreshSubscription.unsubscribe()
      this._appRefreshSubscription = null
    }
  }
}
